import { format, formatDistanceToNow } from 'date-fns';
import { ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { When } from 'react-if';
import { Link } from 'react-router-dom';
import { IStatementBuilderTemplate, IStatementBuilderVersion } from 'shared';

import { Button } from '../../../../../../common/Atoms/Button';
import { Dropdown } from '../../../../../../common/Atoms/Dropdown';
import { Modal } from '../../../../../../common/Atoms/Modal';
import { Notification } from '../../../../../../common/Atoms/Notification';
import { Select } from '../../../../../../common/Atoms/Select';
import { Toggle } from '../../../../../../common/Atoms/Toggle';
import { Heading } from '../../../../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../../../../common/Atoms/Typography/Paragraph';

export type TemplateAction = `create` | `duplicate` | `rename` | `delete`;

interface BuilderHeaderProps {
  templates: IStatementBuilderTemplate[];
  templateId: number;
  onSelectTemplate: (templateId: number) => void;
  onTemplateAction: (action: TemplateAction) => void;
  hasPublished: boolean;
  versions: IStatementBuilderVersion[];
  draftIsPublished: boolean;
  enabled: boolean;
  /** False for AI_STATEMENT_BUILDER_PARTIAL orgs, which can build but not switch statements over */
  canEnable: boolean;
  busy: boolean;
  onToggleEnabled: (enabled: boolean) => Promise<void>;
  onPublish: () => Promise<void>;
  onReset: () => Promise<void>;
}

export function BuilderHeader({
  templates,
  templateId,
  onSelectTemplate,
  onTemplateAction,
  hasPublished,
  versions,
  draftIsPublished,
  enabled,
  canEnable,
  busy,
  onToggleEnabled,
  onPublish,
  onReset,
}: BuilderHeaderProps) {
  const [showVersions, setShowVersions] = useState(false);
  const latest = versions[0];
  const policyCount = templates.find(template => template.id === templateId)?.policyCount || 0;
  const templateOptions = templates.map(template => ({
    label: template.title,
    value: template.id,
    badge: template.enabled ? `Default` : undefined,
  }));
  const publishedText = latest
    ? `Version ${latest.version} published ${formatDistanceToNow(new Date(latest.publishedAt), { addSuffix: true })}${latest.publishedBy ? ` by ${latest.publishedBy}` : ``}`
    : null;

  return (
    <header className={ `rounded-xl border border-slate-200 bg-white px-5 py-4` }>
      <div className={ `flex flex-wrap items-center justify-between gap-4` }>
        <div>
          <Link
            to={ `../statement` }
            className={ `inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700` }
          >
            <ArrowLeftIcon className={ `h-4 w-4` } />
            { `Back to Statement Settings` }
          </Link>
          <div className={ `mt-2 flex items-center gap-2` }>
            <SparklesIcon className={ `h-6 w-6 text-indigo-500` } />
            <Heading size={ `xl` }>
              { `AI Statement Builder` }
            </Heading>
          </div>
          {
            publishedText &&
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
              className={ `mt-1` }
            >
              { publishedText }
              { ` · ` }
              <button
                type={ `button` }
                className={ `cursor-pointer underline hover:text-slate-700` }
                onClick={ () => setShowVersions(true) }
              >
                { `Version history` }
              </button>
            </Paragraph>
          }
        </div>

        <div className={ `flex flex-wrap items-center justify-end gap-3` }>
          <div className={ `w-64` }>
            <Select
              name={ `statementTemplate` }
              options={ templateOptions }
              selected={ templateId }
              disabled={ busy }
              // Match the 32px buttons beside it
              buttonClassname={ `flex h-8 items-center rounded-lg py-0` }
              onChange={ ({ value }) => onSelectTemplate(Number(value)) }
            />
          </div>
          <Dropdown
            label={ `Manage` }
            disabled={ busy }
            size={ `md` }
            buttonColor={ `white` }
            options={ [
              [
                { label: `New template`, value: `create` },
                { label: `Duplicate`, value: `duplicate` },
                { label: `Rename`, value: `rename` },
              ],
              [
                {
                  label: `Delete`,
                  value: `delete`,
                  disabled: policyCount > 0 || templates.length < 2,
                  tooltip: policyCount > 0
                    ? `Used by ${policyCount} ${policyCount === 1 ? `policy` : `policies`}. Pick a different statement template on ${policyCount === 1 ? `it` : `them`} first.`
                    : templates.length < 2 ? `You need at least one template.` : undefined,
                },
              ],
            ] }
            onSelect={ option => onTemplateAction(option.value as TemplateAction) }
          />
          <Button
            color={ `paidniceRedOutline` }
            disabled={ busy }
            onClick={ onReset }
          >
            { `Reset to Base` }
          </Button>
          <Button
            disabled={ busy || draftIsPublished }
            onClick={ onPublish }
          >
            { draftIsPublished ? `Published` : `Publish` }
          </Button>
        </div>
      </div>

      <Modal
        title={ `Version history` }
        open={ showVersions }
        setOpen={ setShowVersions }
      >
        <ul className={ `mt-4 divide-y divide-slate-200` }>
          {
            versions.map(version => (
              <li
                key={ version.id }
                className={ `flex items-center justify-between gap-4 py-3` }
              >
                <div>
                  <Paragraph>
                    { `Version ${version.version}` }
                    { version === latest ? ` (current)` : `` }
                  </Paragraph>
                  <Paragraph
                    size={ `xs` }
                    variant={ `secondary` }
                  >
                    { version.publishedBy ? `Published by ${version.publishedBy}` : `Published` }
                  </Paragraph>
                </div>
                <Paragraph
                  size={ `xs` }
                  variant={ `secondary` }
                >
                  { format(new Date(version.publishedAt), `PPp`) }
                </Paragraph>
              </li>
            ))
          }
        </ul>
      </Modal>

      <div className={ `mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3` }>
        <When condition={ !canEnable }>
          <Notification
            type={ `info` }
            className={ `mb-3` }
          >
            { `Your organisation can design and preview a custom statement in the builder, but using it for customer statements isn't available yet. Customers keep getting the standard template. Contact support if you'd like to start using it.` }
          </Notification>
        </When>
        <Toggle
          name={ `use-custom-statement` }
          checked={ canEnable && enabled }
          disabled={ !canEnable || !hasPublished || busy }
          label={ `Use as default statement` }
          description={ `Statements use the default template unless a statement policy picks a different one. Turning this off on the default switches back to the standard template.` }
          helpText={ hasPublished ? undefined : `Publish this template at least once before making it the default.` }
          onChange={ async({ value }) => {
            await onToggleEnabled(value);
          } }
        />
      </div>
    </header>
  );
}
